import { useState, useCallback } from 'react';
import { useAppDispatch } from './redux';
import { apiClient, AppData } from '../api/client';
import { useApiRequest } from '../api';
import {
    setSelectedCity,
    setEvents,
    setWebcams,
    setMapToken,
    setCentrifugoConnectionToken,
    setCityTag,
    setUserData,
} from '../store/slices/mapSlice';
import { loadSettings } from '../store/slices/settingsSlice';
import { setProfile } from '../store/slices/networkingSlice';
import {
    setCities as setGiftsCities,
    setMyGifts,
    setPictures,
} from '../store/slices/giftsSlice';
import { networkingAPI } from '../api/networkingClient';
import { giftsAPI } from '../api/giftsClient';
import type { City } from '../types';
import { useAppSelector } from './redux';
import { setUserPremium } from '../store/slices/mapSlice';

interface AppInitializationResult extends AppData {
    shouldShowRatingModal?: boolean;
    shouldShowCitySelector?: boolean;
}

export const useAppInitialization = () => {
    const dispatch = useAppDispatch();
    const { request } = useApiRequest();

    const [cities, setCities] = useState<City[]>([]);
    const [contestData, setContestData] = useState<any>(null);
    const [isAppReady, setIsAppReady] = useState(false);
    const { userPremium } = useAppSelector((state) => state.map);
    const [abTests, setAbTests] = useState<Array<{ tag: string, type: string }>>([]);
    const [tariff, setTariff] = useState<{ month: number; half_year: number; year: number } | null>(null);
    const [isEventReportsEnabled, setIsEventReportsEnabled] = useState<boolean>(false);

    const loadAppData = useCallback(async (
        context: "initialization" | "city-change" = "initialization"
    ): Promise<AppInitializationResult | null> => {
        try {
            console.log(`🚀 Загрузка данных (${context})...`);

            const data = await request(apiClient.getAppData);

            if (!data) {
                console.error("❌ Данные не получены");
                return null;
            }

            if (data.tariff) {
                setTariff(data.tariff);
                console.log("💰 Тарифы получены:", data.tariff);
            }
            // Устанавливаем токен карты
            if (data.token) {
                dispatch(setMapToken(data.token));
            }

            if (data._ab_tests) {
                setAbTests(data._ab_tests);
                console.log("🧪 A/B тесты:", data._ab_tests);
            }

            if (data.centrifugo_connection_token) {
                dispatch(setCentrifugoConnectionToken(data.centrifugo_connection_token));
            }

            if (data.city_tag) {
                dispatch(setCityTag(data.city_tag));
            }
            // Устанавливаем данные конкурса
            setContestData(data.contest || null);

            // ВАЖНО: Сначала устанавливаем премиум статус, потом события
            // иначе проверка в setEvents не сработает корректно
            if (data.is_premium !== undefined) {
                dispatch(setUserPremium(data.is_premium));
            }

            // Сохраняем данные пользователя (для ghost mode)
            if (data.user) {
                dispatch(setUserData(data.user));
                console.log("👤 Данные пользователя загружены:", data.user);
            }

            // Устанавливаем события и веб-камеры
            const savedCity = data._cities?.find((city) => city.id === data.city_id);
            dispatch(setEvents({
                events: data.events || [],
                isMailingEnabled: savedCity?.settings.is_mailing_enabled ?? true
            }));
            dispatch(setWebcams(data.webcams || []));

            // Маппинг и загрузка настроек
            const settings = {
                "instant-notifications": data.is_notifications_enabled?.toString() || "false",
                "voice-reports": data.is_voice_reports_enabled?.toString() || "false",
                "quiet-mode": data.is_silent_mode_enabled?.toString() || "false",
                "digest-frequency": data.reports?.toString() || "15",
                summary_enabled: data.reports && data.reports !== -1 ? "true" : "false",
            };

            console.log("📋 Замапленные настройки:", settings);
            dispatch(loadSettings(settings));

            // Устанавливаем города
            if (data._cities?.length > 0) {
                setCities(data._cities);
                dispatch(setGiftsCities(data._cities));
            }

            // Загружаем профиль нетворкинга
            if (context === "initialization") {
                try {
                    console.log("🤝 [NETWORKING] Загружаем профиль...");
                    const profileResponse = await networkingAPI.getProfile();

                    if (profileResponse.success && profileResponse.profile) {
                        console.log("✅ [NETWORKING] Профиль найден:", profileResponse.profile);
                        dispatch(setProfile(profileResponse.profile));
                    } else {
                        console.log("ℹ️ [NETWORKING] Профиль не найден - показываем баннер создания");
                    }
                } catch (error) {
                    console.log("ℹ️ [NETWORKING] Профиль не создан или ошибка загрузки");
                }

                // Загружаем подарки и картинки
                try {
                    console.log("🎁 [GIFTS] Загружаем подарки и картинки...");
                    const [giftsResponse, picturesResponse] = await Promise.all([
                        giftsAPI.getMyGifts(),
                        giftsAPI.getPictures(),
                    ]);

                    dispatch(setMyGifts(giftsResponse.gifts));
                    dispatch(setPictures(picturesResponse.pictures));
                    console.log("✅ [GIFTS] Подарки загружены:", giftsResponse.gifts.length);
                    console.log("✅ [GIFTS] Картинки загружены:", picturesResponse.pictures.length);
                } catch (error) {
                    console.error("❌ [GIFTS] Ошибка загрузки подарков:", error);
                    dispatch(setMyGifts([]));
                    dispatch(setPictures([]));
                }
            }

            // Создаем результат с базовыми данными
            const result: AppInitializationResult = { ...data };

            // Показываем модальное окно рейтинга
            if (data?.show_rating_modal && context === "initialization") {
                result.shouldShowRatingModal = true;
            
}
            // Логика выбора города только при инициализации
            if (context === "initialization") {
                if (data.city_id === 0) {
                    console.log("🏙️ Город не выбран, показываем селектор");
                    result.shouldShowCitySelector = true;
                } else {
                    console.log("🏙️ Город уже выбран:", data.city_id);
                    const savedCity = data._cities?.find((city) => city.id === data.city_id);

                    if (savedCity) {
                        console.log("🏙️ Найден город:", savedCity.name);
                        dispatch(setSelectedCity(savedCity));
                    } else {
                        console.warn("⚠️ Город не найден в списке, показываем селектор");
                        result.shouldShowCitySelector = true;
                    }
                }

                setIsAppReady(true);
            }

            if (data.is_event_reports_enabled !== undefined) {
                setIsEventReportsEnabled(data.is_event_reports_enabled);
                console.log("📝 Репорты событий:", data.is_event_reports_enabled);
            }

            return result;
        } catch (error) {
            console.error(`❌ Ошибка загрузки данных (${context}):`, error);

            if (context === "initialization") {
                setIsAppReady(true);
                return { shouldShowCitySelector: true } as AppInitializationResult;
            }

            return null;
        }
    }, [request, dispatch]);

    return {
        // Состояния
        cities,
        userPremium,
        contestData,
        isAppReady,
        tariff,
        isEventReportsEnabled,

        // Сеттеры
        setCities,

        setContestData,
        setIsAppReady,

        // Методы
        loadAppData,

        //А Б тесты
        abTests,
        setAbTests
    };
};